import React from 'react';
import { Radio } from 'antd';
import { FP120, FP123 } from '../../../services/pwp211Api';

interface Tab1Props {
  formData: FP120;
  basicInfos: FP123[];
  isEditing: boolean;
  getField: (field: keyof FP120) => unknown;
  setField: (field: keyof FP120, value: unknown) => void;
  setBasicInfos: React.Dispatch<React.SetStateAction<FP123[]>>;
}

/* ---- style constants ---- */
const lblStyle: React.CSSProperties = { width: 110, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 };
const inputStyle: React.CSSProperties = { flex: 1, maxWidth: 150, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: '#f5f5f5', color: '#666' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 };

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, basicInfos, isEditing, getField, setField, setBasicInfos,
}) => {
  const handleBasicInfoCheck = (idx: number, checked: boolean) => {
    setBasicInfos(prev => prev.map((bi, i) => i === idx ? { ...bi, fp123_20: checked } : bi));
  };

  const handleBasicInfoField = (idx: number, field: keyof FP123, value: unknown) => {
    setBasicInfos(prev => prev.map((bi, i) => i === idx ? { ...bi, [field]: value } : bi));
  };

  // Get the checked basicInfo row for fp123_16/fp123_17/fp123_18 display
  const checkedInfo = basicInfos.find(bi => bi.fp123_20);

  return (
    <div className="pwp211-tab1">
      {/* FoxPro Page1 fields layout -- matching spatial rows */}

      {/* Row 1: 繳庫/託工入庫單號 (left) | 檢驗時間 (right) */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_24" style={lblStyle}>繳庫/託工入庫單號</span>
          <input
            value={(getField('fp120_24') as string) || ''}
            readOnly
            disabled
            style={inputStyle} data-contract-field="fp120_24"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_26" style={lblStyle}>檢驗時間</span>
          <input
            value={formData.fp120_26 ? formData.fp120_26.substring(0, 19).replace('T', ' ') : ''}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 200 }} data-contract-field="fp120_26"
          />
        </div>
      </div>

      {/* Row 2: 供應廠商 (left) | 檢驗完成時間 (right) */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_10" style={lblStyle}>供應廠商</span>
          <input
            value={(getField('fp120_10') as string) || ''}
            readOnly
            disabled
            style={inputStyle} data-contract-field="fp120_10"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_27" style={lblStyle}>檢驗完成時間</span>
          <input
            value={formData.fp120_27 ? formData.fp120_27.substring(0, 19).replace('T', ' ') : ''}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 200 }} data-contract-field="fp120_27"
          />
        </div>
      </div>

      {/* Row 3: 驗貨標準 | 顏色名稱(fp123_111) | 顏色名稱(fp123_112) | 總項數 */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp123_16" style={lblStyle}>驗貨標準</span>
          <input
            value={checkedInfo?.fp123_16 || ''}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 80 }} data-contract-field="fp123_16"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp123_111" style={lblStyle}>顏色名稱</span>
          <input
            value={checkedInfo?.fp123_111 || ''}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 120 }} data-contract-field="fp123_111"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp123_112" style={lblStyle}>顏色名稱</span>
          <input
            value={checkedInfo?.fp123_112 || ''}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 220 }} data-contract-field="fp123_112"
          />
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_28" style={lblStyle}>總項數</span>
          <input
            value={(getField('fp120_28') as number) || 0}
            readOnly
            disabled
            style={{ ...inputStyle, maxWidth: 60 }} data-contract-field="fp120_28"
          />
        </div>
      </div>

      {/* Row 4: 繳庫日期 */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp120_29" style={lblStyle}>繳庫日期</span>
          <input
            value={(getField('fp120_29') as string) || ''}
            readOnly
            disabled
            style={inputStyle} data-contract-field="fp120_29"
          />
        </div>
      </div>

      {/* Row 5: 種類 / 布種 Option Groups */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp123_17" style={lblStyle}>種類</span>
          <Radio.Group
            data-contract-field="fp123_17"
            value={checkedInfo?.fp123_17 ?? 0}
            onChange={e => {
              if (!isEditing || !checkedInfo) return;
              const idx = basicInfos.findIndex(bi => bi.fp123_20);
              if (idx >= 0) handleBasicInfoField(idx, 'fp123_17', e.target.value);
            }}
            disabled={!isEditing}
          >
            <Radio value={1}>染色</Radio>
            <Radio value={2}>胚布</Radio>
            <Radio value={3}>其他</Radio>
          </Radio.Group>
        </div>
        <div style={rowStyle}>
          <span className="form-label" data-contract-field="fp123_18" style={lblStyle}>布種</span>
          <Radio.Group
            data-contract-field="fp123_18"
            value={checkedInfo?.fp123_18 ?? 0}
            onChange={e => {
              if (!isEditing || !checkedInfo) return;
              const idx = basicInfos.findIndex(bi => bi.fp123_20);
              if (idx >= 0) handleBasicInfoField(idx, 'fp123_18', e.target.value);
            }}
            disabled={!isEditing}
          >
            <Radio value={1}>梭織</Radio>
            <Radio value={2}>針織</Radio>
          </Radio.Group>
        </div>
      </div>

      {/* FP123 基本資料明細 Grid -- FoxPro column order: 項數, 檢驗, 產品代號, 規格, 色號, 採購數量, 已繳庫量, 本批繳庫量 */}
      <div className="detail-section">
        <h4>基本資料明細</h4>
        <table className="detail-table">
          <thead>
            <tr>
              <th data-contract-field="fp123_02">項數</th>
              <th data-contract-field="fp123_20">檢驗</th>
              <th data-contract-field="fp123_10">產品代號</th>
              <th data-contract-field="fp123_19">規格</th>
              <th data-contract-field="fp123_11">色號</th>
              <th data-contract-field="fp123_13">採購數量</th>
              <th data-contract-field="fp123_14">已繳庫量</th>
              <th data-contract-field="fp123_15">本批繳庫量</th>
            </tr>
          </thead>
          <tbody>
            {basicInfos.map((bi, idx) => (
              <tr key={idx}>
                <td>{bi.fp123_02}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={bi.fp123_20}
                    onChange={e => handleBasicInfoCheck(idx, e.target.checked)}
                    disabled={!isEditing} data-contract-field="fp123_20"
                  />
                </td>
                <td>
                  <input
                    value={bi.fp123_10}
                    onChange={e => handleBasicInfoField(idx, 'fp123_10', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp123_10"
                  />
                </td>
                <td>
                  <input
                    value={bi.fp123_19 || ''}
                    onChange={e => handleBasicInfoField(idx, 'fp123_19', e.target.value)}
                    disabled={!isEditing}
                    className="table-input"
                    style={{ minWidth: 180 }} data-contract-field="fp123_19"
                  />
                </td>
                <td>
                  <input
                    value={bi.fp123_11}
                    onChange={e => handleBasicInfoField(idx, 'fp123_11', e.target.value)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp123_11"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={bi.fp123_13}
                    onChange={e => handleBasicInfoField(idx, 'fp123_13', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp123_13"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={bi.fp123_14}
                    readOnly
                    disabled
                    className="table-input readonly" data-contract-field="fp123_14"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={bi.fp123_15}
                    onChange={e => handleBasicInfoField(idx, 'fp123_15', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="table-input" data-contract-field="fp123_15"
                  />
                </td>
              </tr>
            ))}
            {basicInfos.length === 0 && (
              <tr><td colSpan={8} className="no-data">無基本資料明細</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default Tab1BasicInfo;
